import { LocalDate } from '@brunommsilva/react-foundations';

import {
  NewTransaction,
  Transaction,
  TransactionsPage,
  TransactionsSummary,
  UpdateTransaction,
} from '../../../models/transactions';
import AccountingApiClient from '../base-client';
import {
  PageRequest,
  TransactionRequest,
  TransactionResponse,
  TransactionsPageResponse,
} from './types';

class TransactionsApi extends AccountingApiClient<
  Transaction,
  TransactionsPage
> {
  public constructor() {
    super('/transactions');
  }

  private buildFilter = (filter: PageRequest['filter']): string | undefined => {
    if (!filter) {
      return;
    }

    const expressions = [];

    if (filter.type) {
      expressions.push(`type eq '${filter.type}'`);
    }

    if (filter.categoryId) {
      expressions.push(`category eq '${filter.categoryId}'`);
    }

    if (filter.dateFrom) {
      expressions.push(`date gte '${filter.dateFrom.toString()}'`);
    }

    if (filter.accountId) {
      expressions.push(
        `(account eq '${filter.accountId}' or otherAccount eq '${filter.accountId}')`
      );
    }

    if (filter.dateTo) {
      expressions.push(`date lte '${filter.dateTo.toString()}'`);
    }

    if (filter.description) {
      expressions.push(`contains(description, '${filter.description}')`);
    }

    return expressions.join(' and ');
  };

  private toModel = (response: TransactionResponse): Transaction =>
    new Transaction({
      ...response,
      date: new LocalDate(response.date),
      createdAt: new Date(response.createdAt),
      updatedAt: new Date(response.updatedAt),
    });

  public getPage = async ({
    page,
    perPage,
    filter,
  }: PageRequest): Promise<TransactionsPage> => {
    let url = `${this.baseUrl}?`;

    if (page) {
      url = `${url}&page=${page}`;
    }
    if (perPage) {
      url = `${url}&per_page=${perPage}`;
    }

    const filterExpression = this.buildFilter(filter);

    if (filterExpression) {
      url = `${url}&filter=${filterExpression}`;
    }

    const result = await this.fetch<TransactionsPageResponse>(url, {
      method: 'GET',
    });

    return {
      ...result,
      transactions: result.transactions.map(this.toModel),
    };
  };

  public getSummary = async (
    filter: PageRequest['filter']
  ): Promise<TransactionsSummary> => {
    let url = `${this.baseUrl}/summary?`;

    const filterExpression = this.buildFilter(filter);
    if (filterExpression) {
      url = `${url}&filter=${filterExpression}`;
    }

    return this.fetch<TransactionsSummary>(url, { method: 'GET' });
  };

  public get = async (id: string): Promise<Transaction> => {
    const transaction = await this.fetch<TransactionResponse>(
      `${this.baseUrl}/${id}`,
      { method: 'GET' }
    );

    return this.toModel(transaction);
  };

  public create = async (transaction: NewTransaction): Promise<Transaction> => {
    const created = await super.httpPost<
      TransactionRequest,
      TransactionResponse
    >(this.baseUrl, { ...transaction, date: transaction.date.toString() });

    return this.toModel(created);
  };

  public update = async (
    transaction: UpdateTransaction
  ): Promise<Transaction> => {
    const { id, ...body } = transaction;
    const updated = await super.httpPut<
      TransactionRequest,
      TransactionResponse
    >(`${this.baseUrl}/${id}`, { ...body, date: body.date.toString() });

    return this.toModel(updated);
  };

  public delete = async (id: string): Promise<void> => {
    return super.httpDelete(`${this.baseUrl}/${id}`);
  };
}

const transactionsApi = new TransactionsApi();

export default transactionsApi;
